import { Link } from 'expo-router';
import { ScanLineIcon, XIcon } from 'lucide-react-native';
import * as React from 'react';
import { Modal, View } from 'react-native';

import { androidStatusBarHeight, ModalStatusBarBand } from '@/components/android-status-bar-band';
import { MemberQrScanner } from '@/components/qr/member-qr-scanner';
import { Avatar, AvatarFallback, AvatarImage } from '@sixam/ui/avatar';
import { Button } from '@sixam/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@sixam/ui/card';
import { Text } from '@sixam/ui/text';
import {
  addFriend,
  addMemberToEvent,
  displayMemberName,
  resolveMemberScan,
  type MemberScanResult,
} from '@/lib/member-scan';
import type { ParsedMemberQrJwt } from '@/lib/member-qr';

type MemberScanFlowProps = Readonly<{
  eventId?: string;
  scanButtonLabel?: string;
  /** Only needed for the "Add to event" action, which also requires eventId. */
  tenantSlug?: string;
}>;

export function MemberScanFlow({
  eventId,
  scanButtonLabel = 'Scan member QR',
  tenantSlug,
}: MemberScanFlowProps) {
  const [isScannerOpen, setIsScannerOpen] = React.useState(false);
  const [scanResult, setScanResult] = React.useState<MemberScanResult | null>(null);
  const [isResolving, setIsResolving] = React.useState(false);

  const handleScanned = React.useCallback((scan: ParsedMemberQrJwt) => {
    setIsScannerOpen(false);
    setIsResolving(true);
    resolveMemberScan(scan)
      .then(setScanResult)
      .catch((error: unknown) => {
        setScanResult({
          error: error instanceof Error ? error.message : 'Could not read member QR code.',
          status: 'unreadable',
        });
      })
      .finally(() => setIsResolving(false));
  }, []);

  return (
    <View className="gap-4">
      <Button className="w-full" onPress={() => setIsScannerOpen(true)}>
        <ScanLineIcon color="white" size={18} />
        <Text>{scanButtonLabel}</Text>
      </Button>

      {isResolving ? <Text variant="muted">Reading member QR…</Text> : null}

      {scanResult ? (
        <MemberScanResultCard eventId={eventId} result={scanResult} tenantSlug={tenantSlug} />
      ) : null}

      <Modal
        animationType="slide"
        onRequestClose={() => setIsScannerOpen(false)}
        presentationStyle="pageSheet"
        statusBarTranslucent
        visible={isScannerOpen}
      >
        <View
          className="flex-1 gap-4 bg-background p-6"
          style={{ paddingTop: androidStatusBarHeight() + 24 }}
        >
          <ModalStatusBarBand />
          <View className="flex-row items-center justify-between">
            <Text variant="h2">Scan QR</Text>
            <Button
              accessibilityLabel="Close scanner"
              onPress={() => setIsScannerOpen(false)}
              size="icon"
              variant="secondary"
            >
              <XIcon color="white" size={18} />
            </Button>
          </View>
          {isScannerOpen ? <MemberQrScanner active onScanned={handleScanned} /> : null}
        </View>
      </Modal>
    </View>
  );
}

function MemberScanResultCard({
  eventId,
  result,
  tenantSlug,
}: Readonly<{
  eventId?: string;
  result: MemberScanResult;
  tenantSlug?: string;
}>) {
  const [actionMessage, setActionMessage] = React.useState<string | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [isSaving, setIsSaving] = React.useState(false);

  if (result.status !== 'found') {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Could not read</CardTitle>
          <CardDescription>{result.error}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const { member } = result;
  const memberName = displayMemberName(member);
  const initials = memberName.slice(0, 2).toUpperCase();

  async function runAction(action: () => Promise<{ message: string }>) {
    setIsSaving(true);
    setActionError(null);
    setActionMessage(null);
    try {
      const response = await action();
      setActionMessage(response.message);
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Action failed.');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Member found</CardTitle>
        <CardDescription>Choose what to do next.</CardDescription>
      </CardHeader>
      <CardContent className="gap-4">
        <View className="flex-row items-center gap-3">
          <Avatar alt={`${memberName}'s avatar`} className="size-14">
            {member.avatarUrl ? <AvatarImage source={{ uri: member.avatarUrl }} /> : null}
            <AvatarFallback>
              <Text variant="label">{initials}</Text>
            </AvatarFallback>
          </Avatar>
          <View className="min-w-0 flex-1">
            <Text variant="large" numberOfLines={1}>
              {memberName}
            </Text>
            {member.email ? (
              <Text variant="muted" numberOfLines={1}>
                {member.email}
              </Text>
            ) : null}
          </View>
        </View>

        <View className="gap-2">
          <Link
            href={{
              pathname: '/(authenticated)/app/members/[id]',
              params: { id: member.id },
            }}
            asChild
          >
            <Button variant="secondary">
              <Text>View profile</Text>
            </Button>
          </Link>
          <Button disabled={isSaving} onPress={() => runAction(() => addFriend(member.id))}>
            <Text>Add friend</Text>
          </Button>
          {eventId ? (
            <Button
              disabled={isSaving}
              onPress={() => runAction(() => addMemberToEvent(eventId, member.id))}
              variant="outline"
            >
              <Text>Add to event</Text>
            </Button>
          ) : null}
        </View>

        {actionMessage ? <Text variant="muted">{actionMessage}</Text> : null}
        {actionError ? (
          <Text className="text-sm text-destructive" selectable>
            {actionError}
          </Text>
        ) : null}
      </CardContent>
    </Card>
  );
}
